import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import { buildQuery, toastError } from "../helpers";

/**
 * One Dealer API admin list (simple pagination: next_page_url / prev_page_url, no total).
 * `path` is appended to APP_URLS.DEALER_API; `params` are the query filters (empty values are
 * dropped). Only the latest request may update state, so fast filter changes never show rows
 * from an older query. Changing `refreshToken` refetches (an action elsewhere changed the data).
 */
export default function usePagedList(path, params, refreshToken = 0) {
  const [rows, _rows] = useState([]);
  const [loading, _loading] = useState(true);
  const [page, _page] = useState(1);
  const [hasNext, _hasNext] = useState(false);
  const seq = useRef(0);
  const filterKey = buildQuery(params);
  const query = buildQuery({ ...params, page });

  // New filters start again from the first page — and from an empty list. Keeping the previous
  // filter's rows and `hasNext` left the pager offering a "Next" that belonged to the old query,
  // so a click during the reload jumped to a page the new filter may not have.
  useEffect(() => {
    _page(1);
    _rows([]);
    _hasNext(false);
  }, [filterKey]);

  // `page` has already moved by the time a request fails, so keeping the rows would label the
  // previous page's results as the new one — and leave a "Next" that belongs to neither.
  const clear = () => {
    _rows([]);
    _hasNext(false);
  };

  const reload = useCallback(() => {
    const current = ++seq.current;
    _loading(true);
    API.get(`${APP_URLS.DEALER_API}${path}?${query}`)
      .then((res) => {
        if (current !== seq.current) return;
        const resp = res.data;
        if (resp.success === true) {
          _rows(resp.data?.data ?? []);
          _hasNext(!!resp.data?.next_page_url);
        } else {
          clear();
          toast.error(resp.message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch((error) => {
        if (current !== seq.current) return;
        clear();
        toastError(error);
      })
      .finally(() => {
        if (current === seq.current) _loading(false);
      });
  }, [path, query]);

  useEffect(() => {
    reload();
  }, [reload, refreshToken]);

  return { rows, loading, page, setPage: _page, hasPrev: page > 1, hasNext, reload };
}
